/**
 * @file veriomeservices.main.jsx
 * @description application entry point
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 *  https://stackoverflow.com/questions/71668256/deprecation-notice-reactdom-render-is-no-longer-supported-in-react-18
 * 
 * @author don michael
 * @since Sep 14, 2023 21:39
 */
import 'src/index.css';
import * as Sentry from "@sentry/react";

import { createRoot } from "react-dom/client"
import { BrowserRouter } from 'react-router-dom';

import ScrollTop from 'src/hooks/useScrollTop';

import 'nprogress/nprogress.css';

import { Provider } from 'react-redux';

import store from 'src/store';
import App from 'src/App';
import AppErrorBoundry from 'src/errorBoundry/AppErrorBoundry';

import { SidebarProvider } from 'src/contexts/SidebarContext';
import * as serviceWorker from 'src/serviceWorker';

import Status500 from 'src/content/pages/Status/Status500';

const container = document.getElementById("root");
const root = createRoot(container);

const sentryDsn = import.meta.env.VITE_APP_SENTRY_DSN;
let sessionSampleRate = import.meta.env.VITE_APP_SENTRY_SESSION_SAMPLE_RATE;

if (!sessionSampleRate) {
    sessionSampleRate = 0.1;
}

const nodeEnv = import.meta.env.VITE_APP_NODE_ENV;

if (sentryDsn && nodeEnv === 'production') {

    Sentry.init({
        dsn: sentryDsn,
        sendDefaultPii: true,
        enableLogs: true,
        integrations: [
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
            Sentry.replayIntegration(),
        ],
        replaysSessionSampleRate: sessionSampleRate,
        replaysOnErrorSampleRate: 1.0
    });

}

root.render(
    <AppErrorBoundry fallback={<Status500 />}>
        <Provider store={store}>
            <SidebarProvider>
                <BrowserRouter>
                    <ScrollTop />
                    <App />
                </BrowserRouter>
            </SidebarProvider>
        </Provider>
    </AppErrorBoundry>
);

serviceWorker.unregister();
